import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface LinkBuildingDomain {
  id: string;
  brand_id: string;
  user_id: string;
  domain_name: string;
  traffic?: number;
  dr?: number;
  created_at: string;
  updated_at: string;
}

export const useLinkBuildingDomains = (brandId: string, page: number = 1, pageSize: number = 100, searchQuery: string = '') => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['link-building-domains', brandId, page, pageSize, searchQuery],
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from('os_lb_domains')
        .select('*', { count: 'exact' })
        .eq('brand_id', brandId);

      // Add search filter if query exists
      if (searchQuery.trim()) {
        query = query.ilike('domain_name', `%${searchQuery.trim()}%`);
      }

      const { data, error, count } = await query
        .order('domain_name', { ascending: true })
        .range(from, to);

      if (error) throw error;
      return { 
        domains: data as LinkBuildingDomain[], 
        totalCount: count || 0 
      };
    },
    enabled: !!brandId,
  });

  // Fetch global stats
  const { data: statsData } = useQuery({
    queryKey: ['link-building-domains-stats', brandId],
    queryFn: async () => {
      const { count: above60DR, error: drError } = await supabase
        .from('os_lb_domains')
        .select('*', { count: 'exact', head: true })
        .eq('brand_id', brandId)
        .gt('dr', 60);

      const { count: above1kTraffic, error: trafficError } = await supabase
        .from('os_lb_domains')
        .select('*', { count: 'exact', head: true })
        .eq('brand_id', brandId)
        .gt('traffic', 1000);

      if (drError || trafficError) throw drError || trafficError;

      return {
        domainsAbove60DR: above60DR || 0,
        domainsAbove1kTraffic: above1kTraffic || 0
      };
    },
    enabled: !!brandId,
  });

  const domains = data?.domains || [];
  const totalCount = data?.totalCount || 0;
  const domainsAbove60DR = statsData?.domainsAbove60DR || 0;
  const domainsAbove1kTraffic = statsData?.domainsAbove1kTraffic || 0;

  const createDomain = useMutation({
    mutationFn: async (domain: Omit<LinkBuildingDomain, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('os_lb_domains')
        .insert([domain])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['link-building-domains', brandId] });
      queryClient.invalidateQueries({ queryKey: ['link-building-domains-all', brandId] });
      toast({ title: 'Domain created successfully' });
    },
    onError: (error: any) => {
      const isDuplicate = error.message?.includes('duplicate key value') || 
                          error.code === '23505';
      
      if (isDuplicate) {
        toast({ 
          title: 'Domain already exists', 
          description: 'This domain has already been added to your list.',
          variant: 'destructive' 
        });
      } else {
        toast({ 
          title: 'Error creating domain', 
          description: error.message, 
          variant: 'destructive' 
        });
      }
    },
  });

  const updateDomain = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<LinkBuildingDomain> & { id: string }) => {
      const { data, error } = await supabase
        .from('os_lb_domains')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['link-building-domains', brandId] });
      queryClient.invalidateQueries({ queryKey: ['link-building-domains-all', brandId] });
      toast({ title: 'Domain updated successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error updating domain', description: error.message, variant: 'destructive' });
    },
  });

  const deleteDomain = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_lb_domains')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['link-building-domains', brandId] });
      queryClient.invalidateQueries({ queryKey: ['link-building-domains-all', brandId] });
      toast({ title: 'Domain deleted successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error deleting domain', description: error.message, variant: 'destructive' });
    },
  });

  const bulkImportDomains = useMutation({
    mutationFn: async (domains: Omit<LinkBuildingDomain, 'id' | 'created_at' | 'updated_at'>[]) => {
      const { data, error } = await supabase
        .from('os_lb_domains')
        .upsert(domains, {
          onConflict: 'brand_id,domain_name_lower',
          ignoreDuplicates: false
        })
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['link-building-domains', brandId] });
      queryClient.invalidateQueries({ queryKey: ['link-building-domains-stats', brandId] });
      toast({ 
        title: 'Domains imported successfully', 
        description: `${data.length} domain(s) imported/updated` 
      });
    },
    onError: (error: any) => {
      toast({ title: 'Error importing domains', description: error.message, variant: 'destructive' });
    },
  });

  const clearDuplicates = useMutation({
    mutationFn: async () => {
      // Call the database function to safely remove duplicates
      const { data, error } = await supabase.rpc('dedupe_os_lb_domains', {
        p_brand_id: brandId
      });

      if (error) throw error;

      return { deletedCount: data || 0 };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['link-building-domains', brandId] });
      queryClient.invalidateQueries({ queryKey: ['link-building-domains-stats', brandId] });
      toast({ 
        title: 'Duplicates cleared successfully', 
        description: result.deletedCount === 0 
          ? 'No duplicates found' 
          : `${result.deletedCount} duplicate domain(s) removed` 
      });
    },
    onError: (error: any) => {
      toast({ title: 'Error clearing duplicates', description: error.message, variant: 'destructive' });
    },
  });

  return {
    domains,
    totalCount,
    domainsAbove60DR,
    domainsAbove1kTraffic,
    isLoading,
    createDomain,
    updateDomain,
    deleteDomain,
    bulkImportDomains,
    clearDuplicates,
  };
};